import type { BookSettings } from "../types/book";
import { logger } from "./logger";

const READER_POSITION_CHECKPOINT_PREFIX =
  "wereader.reader-position-checkpoint.v1:";

export type ReaderPositionCheckpoint = {
  location?: string;
  progress?: number;
  locationUpdatedAt: number;
};

const finiteTimestamp = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 0;
  }
  return 0;
};

const finiteProgress = (value: unknown): number | undefined =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  value >= 0 &&
  value <= 1
    ? value
    : undefined;

const checkpointStorageKey = (bookId: string): string =>
  `${READER_POSITION_CHECKPOINT_PREFIX}${encodeURIComponent(bookId)}`;

const normalizeCheckpoint = (
  value: unknown,
): ReaderPositionCheckpoint | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as Partial<ReaderPositionCheckpoint>;
  const location =
    typeof candidate.location === "string" && candidate.location.trim()
      ? candidate.location
      : undefined;
  const progress = finiteProgress(candidate.progress);
  const locationUpdatedAt = finiteTimestamp(candidate.locationUpdatedAt);
  if (!locationUpdatedAt || (!location && progress === undefined)) {
    return null;
  }

  return {
    ...(location ? { location } : {}),
    ...(progress !== undefined ? { progress } : {}),
    locationUpdatedAt,
  };
};

export const getReadingPositionTimestamp = (
  settings?: Partial<BookSettings>,
): number => {
  const latestHistoryTimestamp = Array.isArray(settings?.updates)
    ? settings.updates.reduce((latest, update) => {
        if (!Array.isArray(update)) {
          return latest;
        }
        return Math.max(latest, finiteTimestamp(update[0]));
      }, 0)
    : 0;
  return Math.max(
    finiteTimestamp(settings?.locationUpdatedAt),
    latestHistoryTimestamp,
  );
};

export const loadReaderPositionCheckpoint = (
  bookId: string,
): ReaderPositionCheckpoint | null => {
  if (!bookId || typeof localStorage === "undefined") {
    return null;
  }

  try {
    const serialized = localStorage.getItem(checkpointStorageKey(bookId));
    if (!serialized) {
      return null;
    }
    const checkpoint = normalizeCheckpoint(JSON.parse(serialized));
    if (!checkpoint) {
      logger.warn("[reader-position-checkpoint] ignored invalid checkpoint", {
        bookId,
      });
    }
    return checkpoint;
  } catch (error) {
    logger.warn("[reader-position-checkpoint] failed to load checkpoint", {
      bookId,
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
};

export const saveReaderPositionCheckpoint = (
  bookId: string,
  settings: Partial<BookSettings>,
): boolean => {
  if (!bookId || typeof localStorage === "undefined") {
    return false;
  }

  const checkpoint = normalizeCheckpoint(settings);
  if (!checkpoint) {
    logger.warn(
      "[reader-position-checkpoint] skipped checkpoint without a valid position",
      {
        bookId,
        hasLocation: Boolean(settings.location),
        progress: settings.progress ?? null,
        locationUpdatedAt: settings.locationUpdatedAt ?? null,
      },
    );
    return false;
  }

  const previous = loadReaderPositionCheckpoint(bookId);
  if (
    previous &&
    previous.locationUpdatedAt > checkpoint.locationUpdatedAt
  ) {
    logger.debug("[reader-position-checkpoint] kept newer checkpoint", {
      bookId,
      previousTimestamp: previous.locationUpdatedAt,
      skippedTimestamp: checkpoint.locationUpdatedAt,
    });
    return false;
  }

  try {
    localStorage.setItem(
      checkpointStorageKey(bookId),
      JSON.stringify(checkpoint),
    );
    logger.debug("[reader-position-checkpoint] saved", {
      bookId,
      location: checkpoint.location || null,
      progress: checkpoint.progress ?? null,
      locationUpdatedAt: checkpoint.locationUpdatedAt,
    });
    return true;
  } catch (error) {
    logger.warn("[reader-position-checkpoint] failed to save checkpoint", {
      bookId,
      location: checkpoint.location || null,
      progress: checkpoint.progress ?? null,
      locationUpdatedAt: checkpoint.locationUpdatedAt,
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

export const reconcileReaderPositionCheckpoint = (
  settings: BookSettings,
  checkpoint: ReaderPositionCheckpoint | null,
): {
  settings: BookSettings;
  applied: boolean;
  persistedTimestamp: number;
  checkpointTimestamp: number;
} => {
  const persistedTimestamp = getReadingPositionTimestamp(settings);
  const checkpointTimestamp = checkpoint?.locationUpdatedAt || 0;
  if (!checkpoint || checkpointTimestamp <= persistedTimestamp) {
    return {
      settings,
      applied: false,
      persistedTimestamp,
      checkpointTimestamp,
    };
  }

  return {
    settings: {
      ...settings,
      ...(checkpoint.location ? { location: checkpoint.location } : {}),
      ...(checkpoint.progress !== undefined
        ? { progress: checkpoint.progress }
        : {}),
      locationUpdatedAt: checkpoint.locationUpdatedAt,
    },
    applied: true,
    persistedTimestamp,
    checkpointTimestamp,
  };
};
